import { useEffect, useRef } from 'react';

/**
 * Surface an OAuth-callback error delivered via the URL (?error=...).
 *
 * Every channel callback redirects failures back to its dashboard page with an
 * `error` query param (e.g. the partial-consent guard's "requires both
 * permissions" message). The pages only rendered their own in-page API errors,
 * so those callback errors were silently swallowed. This hook reads the param
 * once on mount, hands it to the page's existing error state (rendered by the
 * dismissible critical Banner), and strips it from the iframe URL so an
 * in-iframe refresh does not re-show a stale error.
 */
export function useOAuthCallbackError(onError: (message: string) => void): void {
  const consumed = useRef(false);
  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (!error) return;
    onError(error);
    params.delete('error');
    const qs = params.toString();
    try {
      window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    } catch {
      // Sandboxed iframe may deny history mutation; the error is already shown.
    }
  }, [onError]);
}
